import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Copy, CheckCircle, ExternalLink, AlertTriangle, Hexagon, RefreshCw, Link2, Rocket, Mail } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * Admin panel for managing the Polygon ERC-1155 registry.
 * - Shows platform wallet + balance so admin knows when to fund.
 * - Lists recent on-chain mints with Polygonscan links.
 * - Lets admin POST the deployed registry contract address after deploying
 *   AssagartRegistry.sol via Remix.
 */
export const BlockchainAdminPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [contractInput, setContractInput] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (opts.silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await api.getBlockchainRegistry();
      setData(res.data);
      if (res.data?.contract_address) setContractInput(res.data.contract_address);
    } catch (err) {
      toast.error('Unable to load blockchain registry');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyWallet = async () => {
    if (!data?.platform_wallet) return;
    await navigator.clipboard.writeText(data.platform_wallet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSaveContract = async () => {
    const trimmed = contractInput.trim();
    if (!trimmed) {
      toast.error('Paste the deployed contract address');
      return;
    }
    setSaving(true);
    try {
      await api.setRegistryContractAddress(trimmed);
      toast.success('Registry contract address saved');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contract address');
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (deploying) return;
    if (!window.confirm(
      'Deploy AssagartRegistry.sol to Polygon Amoy using the platform wallet? ' +
      'This will consume a small amount of test POL and can take up to 2 minutes.'
    )) return;
    setDeploying(true);
    const toastId = toast.loading('Deploying registry contract to Polygon Amoy… (up to 2 minutes)');
    try {
      const res = await api.deployRegistryContract();
      toast.success(`Registry deployed at ${res.data.contract_address.slice(0, 10)}…`, { id: toastId });
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deployment failed', { id: toastId });
    } finally {
      setDeploying(false);
    }
  };

  const handleSendTestReceipt = async () => {
    if (sendingTest) return;
    setSendingTest(true);
    const toastId = toast.loading('Sending preview mint receipt to your admin email…');
    try {
      const res = await api.sendTestMintReceipt();
      toast.success(res.data?.message || 'Preview sent — check your inbox.', { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send preview email', { id: toastId });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="blockchain-admin-loading">
        <Loader2 size={24} className="text-gold animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { network, network_key, explorer, faucet, connected, contract_deployed,
          contract_address, platform_wallet, platform_wallet_balance,
          platform_wallet_balance_symbol, total_tokens_minted, recent_mints = [] } = data;

  const walletUrl = platform_wallet ? `${explorer}/address/${platform_wallet}` : null;
  const contractUrl = contract_address ? `${explorer}/address/${contract_address}` : null;

  return (
    <div className="space-y-5" data-testid="blockchain-admin-panel">
      {/* Network banner */}
      <div className="luxury-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
          <div>
            <p className="text-white text-sm font-medium">{network}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {connected ? 'RPC reachable' : 'RPC unreachable'} · {network_key === 'mainnet' ? 'Mainnet' : 'Testnet'}
            </p>
          </div>
        </div>
        <Button
          onClick={() => load({ silent: true })}
          size="sm"
          variant="ghost"
          className="text-muted-foreground hover:text-white"
          data-testid="blockchain-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Platform wallet */}
      <div className="luxury-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-gold uppercase tracking-wider">Platform Wallet</p>
          <span className="text-xs text-white font-mono">
            {platform_wallet_balance?.toFixed(4) ?? '0'} {platform_wallet_balance_symbol || 'POL'}
          </span>
        </div>
        {platform_wallet ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-xs text-white font-mono truncate">
              {platform_wallet}
            </code>
            <button
              onClick={copyWallet}
              className="p-2 bg-[#0F0F0F] border border-[#1A1A1A] rounded hover:border-gold/30 transition-colors"
              title="Copy address"
              data-testid="copy-platform-wallet-btn"
            >
              {copied ? <CheckCircle size={14} className="text-emerald-400" /> : <Copy size={14} className="text-muted-foreground" />}
            </button>
            {walletUrl && (
              <a
                href={walletUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#0F0F0F] border border-[#1A1A1A] rounded hover:border-[#627EEA]/40 transition-colors"
                title="View on Polygonscan"
                data-testid="view-platform-wallet-btn"
              >
                <ExternalLink size={14} className="text-[#627EEA]" />
              </a>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No platform wallet yet — it will be auto-generated on next initialisation.</p>
        )}
        {platform_wallet_balance === 0 && faucet && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
            <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-300 leading-relaxed">
              Platform wallet needs test {platform_wallet_balance_symbol || 'POL'} to submit mint transactions.{' '}
              <a href={faucet} target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-200">
                Request from the {network.includes('Amoy') ? 'Amoy' : 'Polygon'} faucet
              </a>{' '}and send it to the address above.
            </div>
          </div>
        )}
      </div>

      {/* Registry contract */}
      <div className="luxury-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-gold uppercase tracking-wider">Registry Contract</p>
          {contract_deployed ? (
            <span className="flex items-center gap-1 text-[10px] text-emerald-400">
              <CheckCircle size={10} /> LINKED
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] text-amber-400">
              <AlertTriangle size={10} /> PENDING DEPLOYMENT
            </span>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={contractInput}
              onChange={(e) => setContractInput(e.target.value)}
              placeholder="0x… Paste deployed AssagartRegistry address"
              className="luxury-input text-xs font-mono"
              data-testid="registry-contract-input"
            />
            <Button
              onClick={handleSaveContract}
              disabled={saving}
              size="sm"
              className="bg-gold text-black hover:bg-gold/90"
              data-testid="save-registry-contract-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
              <span className="ml-1 text-xs">Link</span>
            </Button>
          </div>
          {contractUrl && (
            <a
              href={contractUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-[#627EEA] hover:text-[#7B94F0] transition-colors w-fit"
            >
              <ExternalLink size={10} /> View contract on Polygonscan
            </a>
          )}
        </div>
        {!contract_deployed && (
          <div className="mt-3 space-y-3">
            <div className="p-3 bg-gradient-to-br from-gold/10 to-[#627EEA]/5 border border-gold/30 rounded">
              <div className="flex items-start gap-3">
                <Rocket size={16} className="text-gold flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-white text-xs font-medium mb-1">One-click deploy (recommended)</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">
                    Deploy the registry directly from the platform wallet. Requires at least 0.05 POL balance — you&apos;re {platform_wallet_balance >= 0.05 ? 'good to go.' : 'short on POL, top up via the faucet first.'}
                  </p>
                  <Button
                    onClick={handleDeploy}
                    disabled={deploying || platform_wallet_balance < 0.05}
                    className="bg-gradient-to-r from-gold to-[#F4D03F] text-black hover:from-[#F4D03F] hover:to-gold text-xs font-medium"
                    data-testid="deploy-registry-btn"
                  >
                    {deploying ? (
                      <>
                        <Loader2 size={14} className="animate-spin mr-1" />
                        Deploying…
                      </>
                    ) : (
                      <>
                        <Rocket size={14} className="mr-1" />
                        Deploy Registry
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <details className="group">
              <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-white transition-colors">
                Prefer manual deployment via Remix? Click to expand.
              </summary>
              <div className="mt-2 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-[11px] text-muted-foreground leading-relaxed space-y-1">
                <p className="text-white font-medium">Manual deploy (one-time):</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open <a href="https://remix.ethereum.org" target="_blank" rel="noopener noreferrer" className="text-[#627EEA] underline">remix.ethereum.org</a> and paste <code className="text-gold">/app/backend/app/contracts/AssagartRegistry.sol</code></li>
                  <li>Compile with Solidity 0.8.20 (default settings)</li>
                  <li>In MetaMask, switch to <span className="text-white">{network}</span> and fund the deployer wallet</li>
                  <li>In Remix, select <span className="text-white">Injected Provider — MetaMask</span> and deploy with constructor arg = platform wallet above</li>
                  <li>Paste the deployed contract address into the field above and click <span className="text-gold">Link</span></li>
                </ol>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Stats + recent mints */}
      <div className="luxury-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-gold uppercase tracking-wider">Recent On-Chain Mints</p>
          <div className="flex items-center gap-3">
            {contract_deployed && recent_mints.length > 0 && (
              <button
                onClick={handleSendTestReceipt}
                disabled={sendingTest}
                className="flex items-center gap-1.5 text-[10px] text-[#627EEA] hover:text-[#7B94F0] disabled:opacity-40 transition-colors"
                title="Send a preview of the holder-facing mint receipt email to your admin inbox"
                data-testid="send-test-receipt-btn"
              >
                {sendingTest ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
                Preview receipt email
              </button>
            )}
            <span className="flex items-center gap-1 text-xs text-white">
              <Hexagon size={12} className="text-gold" /> {total_tokens_minted} total
            </span>
          </div>
        </div>
        {recent_mints.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No registry mints yet. Mints happen automatically when you approve tokenisation requests — provided the contract is linked and the platform wallet has gas.</p>
        ) : (
          <div className="space-y-2">
            {recent_mints.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded">
                <div className="min-w-0">
                  <p className="text-white text-xs truncate">{m.asset_name || m.id?.slice(0, 8)}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate">
                    Token #{m.token_id ?? '—'} · tx {m.tx_hash?.slice(0, 10)}…
                  </p>
                </div>
                {m.tx_hash && (
                  <a
                    href={`${explorer}/tx/${m.tx_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#627EEA] hover:text-[#7B94F0] flex-shrink-0 ml-2"
                    title="View transaction"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockchainAdminPanel;
